// Per-profile settings editor. Mirrors the desktop's SettingsModal visually
// (tg-overlay dim + tg-pane card, same input classes, same section layout).
// Form fields map 1:1 to AppSettings; save goes through the orchestrator
// proxy to the child backend's /settings route.
//
// Scope is narrower than the desktop's modal: profile CRUD + logout live in
// the sidebar, theme toggle lives in the top-right, so those panels aren't
// repeated here.
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
	AppSettings,
	AppSettingsSnapshot,
} from "@shared/api/schemas/settings";
import { ApiError, httpClient } from "../../client/http-client";
import { CloseIcon } from "../../components/Icons";

const inputClassName =
	"tg-input tg-text-primary h-10 w-full rounded-[14px] px-3.5 text-[0.92rem]";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-10 items-center justify-center rounded-xl px-4 text-[0.9rem] font-semibold disabled:cursor-wait disabled:opacity-60";
const secondaryButtonClassName =
	"tg-btn-secondary inline-flex h-10 items-center rounded-xl px-3 text-[0.85rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55";

type FieldDescriptor = {
	key: keyof AppSettings;
	label: string;
	type: "text" | "password" | "number" | "boolean";
	placeholder?: string;
	helperText?: string;
};

type SectionDescriptor = {
	id: string;
	title: string;
	description: string;
	fields: FieldDescriptor[];
};

// Web modal exposes the per-profile backend runtime fields and the
// optional Telegram routing destinations. Telegram API credentials
// (telegramApiId / telegramApiHash) are intentionally NOT exposed here —
// they're a deployment-level secret injected by the orchestrator into
// every spawned child, and the runtime always prefers the env over the
// per-profile settings file for those keys.
const SECTIONS: SectionDescriptor[] = [
	{
		id: "connection",
		title: "Connection",
		description:
			"Core runtime values for how this profile's backend listens, stores data, and authenticates with OpenAI.",
		fields: [
			{
				key: "serverPort",
				label: "Server Port",
				type: "number",
				placeholder: "3001",
				helperText:
					"Loopback port the backend binds to. The orchestrator picks this automatically when it spawns the child, so changing it here only matters for direct CLI runs.",
			},
			{
				key: "databaseUrl",
				label: "Database URL",
				type: "text",
				placeholder: "file:./signals.db",
			},
			{
				key: "openAiApiKey",
				label: "OpenAI API Key",
				type: "password",
				placeholder: "sk-...",
			},
		],
	},
	{
		id: "telegram-routing",
		title: "Telegram Routing (optional)",
		description:
			"Non-trading Telegram destinations — only needed for crypto/memecoin flows.",
		fields: [
			{
				key: "telegramMemeCoinChannelName",
				label: "Meme Coin Channel",
				type: "text",
				placeholder: "Yeezus’ Prophets",
			},
			{
				key: "telegramMaestroBot",
				label: "Maestro Bot Chat",
				type: "text",
				placeholder: "Maestro",
			},
		],
	},
	{
		id: "runtime-behavior",
		title: "Runtime Behavior",
		description:
			"Operational flags controlling logging paths and simulation routes.",
		fields: [
			{
				key: "tradingAgentLogDir",
				label: "Log Directory Override",
				type: "text",
				placeholder: "/absolute/path/to/logs",
				helperText:
					"Leave blank to use the default per-profile logs directory inside the orchestrator data dir.",
			},
			{
				key: "enableSimulationDebugRoutes",
				label: "Enable Simulation Debug Routes",
				type: "boolean",
				helperText:
					"Exposes /debug/simulate-* routes on the local backend for testing signal flow without a live account.",
			},
		],
	},
];

// Fields where a change needs the backend restarted to take effect. Save()
// surfaces a reminder banner whenever any of these are in the diff.
const RESTART_REQUIRED_KEYS = new Set<keyof AppSettings>([
	"serverPort",
	"databaseUrl",
	"openAiApiKey",
	"telegramMemeCoinChannelName",
	"telegramMaestroBot",
	"tradingAgentLogDir",
	"enableSimulationDebugRoutes",
]);

interface SettingsModalProps {
	profileId: string;
	isOpen: boolean;
	onClose: () => void;
}

export const SettingsModal = ({
	profileId,
	isOpen,
	onClose,
}: SettingsModalProps) => {
	const [snapshot, setSnapshot] = useState<AppSettingsSnapshot | null>(null);
	const [draft, setDraft] = useState<Partial<AppSettings>>({});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [restarting, setRestarting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [savedAt, setSavedAt] = useState<Date | null>(null);
	const [restartHint, setRestartHint] = useState(false);

	// Fetch when opened.
	useEffect(() => {
		if (!isOpen) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setSavedAt(null);
		setRestartHint(false);
		httpClient.settings
			.get(profileId)
			.then((next) => {
				if (cancelled) return;
				setSnapshot(next);
				setDraft({});
				setLoading(false);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : String(err));
				setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, profileId]);

	// Close on Escape.
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (ev: KeyboardEvent) => {
			if (ev.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [isOpen, onClose]);

	const current: AppSettings | null = useMemo(() => {
		if (!snapshot) return null;
		return { ...snapshot.settings, ...draft };
	}, [snapshot, draft]);

	const isDirty = Object.keys(draft).length > 0;

	const updateField = useCallback(
		<K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
			setDraft((prev) => {
				if (!snapshot) return { ...prev, [key]: value };
				if (snapshot.settings[key] === value) {
					const next = { ...prev };
					delete next[key];
					return next;
				}
				return { ...prev, [key]: value };
			});
		},
		[snapshot],
	);

	const handleSave = useCallback(async () => {
		if (!isDirty) return;
		setSaving(true);
		setError(null);
		const changedKeys = Object.keys(draft) as Array<keyof AppSettings>;
		const needsRestart = changedKeys.some((key) =>
			RESTART_REQUIRED_KEYS.has(key),
		);
		try {
			const response = await httpClient.settings.save(profileId, draft);
			setSnapshot(response);
			setRestartHint(needsRestart);
			setDraft({});
			setSavedAt(new Date());
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
			setSaving(false);
			return;
		}
		setSaving(false);

		// If credentials / runtime config changed, recycle the child backend
		// so it boots with the new settings.json — and then wake up the
		// TelegramSessionService so it reads credentials and emits a fresh
		// state via WS. The TelegramLoginCard subscribes to that channel and
		// will update without a page refresh.
		if (!needsRestart) return;
		setRestarting(true);
		try {
			await httpClient.backend.restart(profileId);
			// Best-effort kick — failure here is non-fatal; the TelegramLogin
			// card retries on its own when the user interacts.
			await httpClient.telegram.getSession(profileId).catch(() => undefined);
			// Auto-close the modal so the user immediately sees the now-updated
			// Telegram card behind it. Brief delay so the success banner is
			// visible for a beat first.
			setTimeout(() => onClose(), 600);
		} catch (err) {
			setError(
				`Settings saved, but auto-restart failed: ${
					err instanceof ApiError ? err.message : String(err)
				}`,
			);
		} finally {
			setRestarting(false);
		}
	}, [draft, isDirty, profileId, onClose]);

	if (!isOpen) return null;

	return (
		<div
			className="tg-overlay fixed inset-0 z-50 grid place-items-center p-6"
			// biome-ignore lint/a11y/noStaticElementInteractions: click-outside to close
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<div
				className="tg-pane tg-text-primary grid w-full max-w-[720px] grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-[18px] shadow-[var(--shadow-tg-elevated)]"
				style={{ maxHeight: "calc(100vh - 48px)" }}
			>
				<header className="flex items-start justify-between gap-4 border-b border-[color:var(--color-tg-line)] px-6 py-4">
					<div>
						<span className="tg-text-secondary mb-1 inline-flex text-[0.7rem] font-semibold uppercase tracking-[0.14em]">
							Profile settings
						</span>
						<h2 className="tg-text-primary text-[1.4rem] font-bold leading-tight">
							Configure this profile
						</h2>
						{snapshot ? (
							<p className="tg-text-muted mt-1 break-all font-mono text-[0.78rem]">
								{snapshot.filePath}
							</p>
						) : null}
					</div>
					<button
						type="button"
						onClick={onClose}
						className="tg-btn-icon tg-btn-secondary tg-text-secondary inline-flex h-9 w-9 rounded-xl"
						aria-label="Close"
						title="Close"
					>
						<CloseIcon className="h-5 w-5" />
					</button>
				</header>

				<div className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto]">
					<div className="tg-scrollbar overflow-y-auto px-6 py-5">
						{loading ? (
							<div className="tg-text-muted py-8 text-center text-[0.9rem]">
								Loading settings…
							</div>
						) : !current ? (
							<div className="tg-alert-error rounded-xl px-4 py-3">
								{error ?? "Could not load settings."}
							</div>
						) : (
							<div className="grid gap-6">
								{SECTIONS.map((section) => (
									<section key={section.id} className="grid gap-3">
										<div>
											<h3 className="tg-text-primary text-[1rem] font-semibold leading-5">
												{section.title}
											</h3>
											<p className="tg-text-muted mt-1 text-[0.85rem] leading-6">
												{section.description}
											</p>
										</div>
										<div className="grid gap-3">
											{section.fields.map((field) => {
												const value = current[field.key];
												return (
													<FieldRow
														key={field.key}
														descriptor={field}
														value={value}
														onChange={(next) =>
															updateField(field.key, next as never)
														}
													/>
												);
											})}
										</div>
									</section>
								))}
							</div>
						)}
					</div>

					<footer className="grid gap-3 border-t border-[color:var(--color-tg-line)] px-6 py-4">
						{restarting ? (
							<div className="tg-alert-warning rounded-xl px-4 py-3 text-[0.85rem] leading-5">
								Restarting the backend so the new settings take effect…
							</div>
						) : restartHint && savedAt ? (
							<div className="tg-alert-success rounded-xl px-4 py-3 text-[0.85rem] leading-5">
								Saved at {savedAt.toLocaleTimeString()}. Backend restarted —
								the Telegram card will update automatically.
							</div>
						) : savedAt ? (
							<div className="tg-alert-success rounded-xl px-4 py-3 text-[0.85rem]">
								Saved at {savedAt.toLocaleTimeString()}.
							</div>
						) : null}
						{error ? (
							<div className="tg-alert-error rounded-xl px-4 py-3 text-[0.85rem]">
								{error}
							</div>
						) : null}
						<div className="flex flex-wrap items-center justify-between gap-2">
							<span className="tg-text-muted text-[0.82rem]">
								{isDirty
									? `${Object.keys(draft).length} pending change${Object.keys(draft).length === 1 ? "" : "s"}`
									: "No pending changes"}
							</span>
							<div className="flex items-center gap-2">
								<ExportImportButtons
									profileId={profileId}
									onImported={(snap) => setSnapshot(snap)}
								/>
								<button
									type="button"
									onClick={onClose}
									className={secondaryButtonClassName}
									disabled={saving || restarting}
								>
									Close
								</button>
								<button
									type="button"
									onClick={() => void handleSave()}
									className={primaryButtonClassName}
									disabled={!isDirty || saving || restarting}
								>
									{saving
										? "Saving…"
										: restarting
											? "Restarting backend…"
											: "Save changes"}
								</button>
							</div>
						</div>
					</footer>
				</div>
			</div>
		</div>
	);
};

interface FieldRowProps {
	descriptor: FieldDescriptor;
	value: unknown;
	onChange: (next: unknown) => void;
}

const FieldRow = ({ descriptor, value, onChange }: FieldRowProps) => {
	if (descriptor.type === "boolean") {
		const checked = Boolean(value);
		return (
			<label className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[color:var(--color-tg-line)] px-3.5 py-2.5">
				<span className="grid gap-1">
					<span className="tg-text-primary text-[0.9rem] font-semibold">
						{descriptor.label}
					</span>
					{descriptor.helperText ? (
						<span className="tg-text-muted text-[0.8rem] leading-5">
							{descriptor.helperText}
						</span>
					) : null}
				</span>
				<span className="relative inline-flex h-6 w-11 shrink-0">
					<input
						type="checkbox"
						className="peer sr-only"
						checked={checked}
						onChange={(event) => onChange(event.target.checked)}
					/>
					<span className="tg-switch-track absolute inset-0 rounded-full transition" />
					<span
						className={`tg-switch-thumb absolute top-1 h-4 w-4 rounded-full transition ${checked ? "left-6" : "left-1"}`}
					/>
				</span>
			</label>
		);
	}

	const inputType =
		descriptor.type === "password"
			? "password"
			: descriptor.type === "number"
				? "text" // allow empty string; stored as string
				: "text";

	return (
		<label className="grid gap-2">
			<span className="tg-text-primary text-[0.9rem] font-semibold">
				{descriptor.label}
			</span>
			<input
				className={inputClassName}
				type={inputType}
				value={typeof value === "string" ? value : ""}
				placeholder={descriptor.placeholder}
				onChange={(event) => onChange(event.target.value)}
				autoComplete={descriptor.type === "password" ? "new-password" : "off"}
			/>
			{descriptor.helperText ? (
				<span className="tg-text-muted text-[0.8rem] leading-5">
					{descriptor.helperText}
				</span>
			) : null}
		</label>
	);
};

interface ExportImportButtonsProps {
	profileId: string;
	onImported: (snapshot: AppSettingsSnapshot) => void;
}

const ExportImportButtons = ({
	profileId,
	onImported,
}: ExportImportButtonsProps) => {
	const handleExport = async () => {
		try {
			const data = await httpClient.settings.exportProfile(profileId);
			const blob = new Blob([JSON.stringify(data, null, 2)], {
				type: "application/json",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			const slug = (data.profileName || "profile").replace(/[^a-z0-9]+/gi, "-");
			a.download = `${slug}.signallens.json`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch (err) {
			window.alert(
				`Export failed: ${err instanceof ApiError ? err.message : String(err)}`,
			);
		}
	};

	const handleImport = async (file: File) => {
		try {
			const text = await file.text();
			const parsed = JSON.parse(text) as {
				channels?: unknown[];
				version?: number;
			};
			if (!Array.isArray(parsed.channels)) {
				window.alert("Invalid export file — missing `channels` array.");
				return;
			}
			const mode =
				window.confirm(
					"Replace existing channels? Click OK to REPLACE, Cancel to MERGE.",
				)
					? "replace"
					: "merge";
			const snap = await httpClient.settings.importProfile(profileId, {
				channels: parsed.channels,
				mode,
			});
			onImported(snap);
		} catch (err) {
			window.alert(
				`Import failed: ${err instanceof ApiError ? err.message : String(err)}`,
			);
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={() => void handleExport()}
				className="tg-btn-secondary inline-flex h-10 items-center rounded-xl px-3 text-[0.85rem] font-semibold"
			>
				Export
			</button>
			<label className="tg-btn-secondary inline-flex h-10 cursor-pointer items-center rounded-xl px-3 text-[0.85rem] font-semibold">
				<input
					type="file"
					accept="application/json,.json"
					className="hidden"
					onChange={(e) => {
						const f = e.target.files?.[0];
						if (f) {
							void handleImport(f);
							e.target.value = "";
						}
					}}
				/>
				Import
			</label>
		</>
	);
};

